import { HttpContext } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { Observable, catchError, map, retry } from 'rxjs';
import { DataProvider } from '../basic/data.service';

/**
 * Removes the `gs.ocs.auth` query parameter from an URL, regardless of its position,
 * while preserving all other query parameters and the URL structure.
 * @param url The full URL to strip the auth token from.
 * @returns The URL without the `gs.ocs.auth` query parameter.
 */
export function removeAuthTokenFromUrl(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.delete('gs.ocs.auth');
    return parsed.toString();
  } catch {
    return url;
  }
}

export interface ILogin {
  user: string;
  password: string;
  modules: string[];
}

export interface ICookieBasket {
  watchListCount: number;
  requestListCount: number;
  albumsCount: number;
  basketCount: number;
  basketAmount: number;
  basketId: number;
  guest: boolean;
}

export interface ICookie {
  basket?: ICookieBasket;
  userId?: string;
  sessionId?: string;
  userAuth?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthProvider extends DataProvider<never> {
  // Services
  private cookieService = inject(CookieService);
  private cookieServiceSSR = inject(SsrCookieService);

  // Properties & Signals
  /** Reactive login state */
  isLoggedIn = signal<boolean>(false);
  /** The endpoint for authentication related requests */
  override endpoint = 'f/account';

  // Methods
  /**
   * Retrieves information about the current authentication state.
   * @param context Optional request context, e.g. to handle errors silently
   * @returns Observable with auth info
   */
  info(context?: HttpContext): Observable<any> {
    return this.http
      .get(this.url + `${this.endpoint}/info`, { ...this.httpOptionsWithContent, context })
      .pipe(
        map((data) => {
          return DataProvider.mapCasesReverse(data);
        })
      )
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Retrieves authentication related cookies.
   * @param ssr Whether to use SSR cookie service.
   * @returns ICookie object with auth cookie data
   */
  cookies(ssr?: boolean): ICookie {
    const cookieServiceUsed = ssr ? this.cookieServiceSSR : this.cookieService;
    const basketEncoded = cookieServiceUsed.get('gs.ocs.identifier.basket');
    let basket: ICookieBasket | undefined;
    if (basketEncoded) {
      basket = JSON.parse(decodeURIComponent(basketEncoded)) as ICookieBasket;
      basket = DataProvider.mapCasesReverse(basket);
    }
    const userId = cookieServiceUsed.get('gs.ocs.identifier.user');
    const sessionId = cookieServiceUsed.get('gs.ocs.session.id');
    const userAuth = cookieServiceUsed.get('gs.ocs.auth');
    return {
      basket,
      userId,
      sessionId,
      userAuth
    };
  }

  /**
   * Logs out the user by sending logout request and clearing auth cookies
   * @param ssr Whether to use SSR cookie service.
   * @returns Observable with redirect URL after logout
   */
  logout(ssr?: boolean): Observable<{ redirectUrl: string }> {
    return this.http
      .post<{ redirectUrl: string }>(`${this.endpointWithUrl}/logout`, {}, this.httpOptionsWithContent)
      .pipe(
        map((data) => {
          this.deleteAuthCookies(ssr);
          return DataProvider.mapCasesReverse(data);
        })
      )
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Checks whether the user is authenticated based on the presence of the auth cookie.
   * @param ssr Whether to use SSR cookie service.
   * @returns {boolean} Returns true if the user is authenticated (auth cookie is present), false otherwise.
   */
  checkAuth(ssr?: boolean): boolean {
    const authCookie = ssr ? this.cookieServiceSSR.get('gs.ocs.auth') : this.cookieService.get('gs.ocs.auth');
    this.isLoggedIn.set(!!authCookie);
    return !!authCookie;
  }

  /**
   * Deletes the authentication related cookies. (also sets isLoggedIn to false)
   * @param ssr Whether to use SSR cookie service.
   */
  deleteAuthCookies(ssr?: boolean): void {
    if (ssr) {
      this.cookieServiceSSR.delete('gs.ocs.auth', '/');
      this.cookieServiceSSR.delete('gs.ocs.session.id', '/');
      this.cookieServiceSSR.delete('gs.ocs.identifier.basket', '/');
    } else {
      this.cookieService.delete('gs.ocs.auth', '/');
      this.cookieService.delete('gs.ocs.session.id', '/');
      this.cookieService.delete('gs.ocs.identifier.basket', '/');
    }
    // Update reactive login state
    this.isLoggedIn.set(false);
  }
}
